import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { getCurrentCompanyId } from '../lib/company';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Truck, MapPin, ExternalLink, FileText, ArrowUp, ArrowDown, Hash, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_BADGES = {
  procesada: { color: 'bg-blue-100 text-blue-800', label: 'Lista para despacho' },
  pendiente_despacho: { color: 'bg-orange-100 text-orange-800', label: 'Pendiente despacho' },
  en_transito: { color: 'bg-orange-100 text-orange-800', label: 'En tránsito' },
  despachado: { color: 'bg-green-100 text-green-800', label: 'Despachado' }
};

export default function DispatchQueue() {
  const [queue, setQueue] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQueue();
  }, []);

  const loadQueue = async () => {
    try {
      const cid = getCurrentCompanyId();
      const [queueRes, clientsRes] = await Promise.all([
        api.getDispatchQueue({ company_id: cid || undefined }),
        api.getClients({ company_id: cid || undefined })
      ]);
      const cmap = {};
      clientsRes.data.forEach(c => { cmap[c.client_id] = c; });
      setClientsMap(cmap);
      setQueue(queueRes.data);
    } catch (e) {
      toast.error('SalesPRO: Error al cargar cola de despacho');
    } finally {
      setLoading(false);
    }
  };

  const updateOrder = async (saleId, newOrder) => {
    try {
      await api.updateDispatchInfo(saleId, { dispatch_order: parseInt(newOrder) });
      toast.success('SalesPRO: Orden actualizado');
      loadQueue();
    } catch (e) {
      toast.error('SalesPRO: Error al actualizar orden');
    }
  };

  const updateStatus = async (saleId, newStatus) => {
    try {
      await api.updateDispatchInfo(saleId, { status: newStatus });
      const labels = { en_transito: 'En tránsito', despachado: 'Entregado' };
      toast.success(`SalesPRO: Pedido marcado como "${labels[newStatus] || newStatus}". Se notificó a admin, master, vendedor y cliente.`);
      loadQueue();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'SalesPRO: Error al actualizar estado');
    }
  };

  const openMaps = (client) => {
    if (!client) return;
    const { lat, lng } = client.location || {};
    const url = (lat && lng)
      ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(client.address)}`;
    window.open(url, '_blank');
  };

  const downloadDispatchTicket = async (saleId, invoice) => {
    try {
      const res = await api.getDispatchTicket(saleId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('SalesPRO: Error al descargar guía');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full">Cargando SalesPRO...</div>;

  return (
    <div className="space-y-6" data-testid="dispatch-queue-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#022C22]">Cola de Despacho</h1>
        <p className="text-[#64748B] mt-1">Asignaciones en orden cronológico — toca la dirección para abrir Google Maps</p>
      </div>

      {queue.length === 0 ? (
        <div className="text-center py-12">
          <Truck size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">No hay despachos pendientes</p>
        </div>
      ) : (
        <div className="space-y-3">
          {queue.map((sale, idx) => {
            const client = clientsMap[sale.client_id];
            const badge = STATUS_BADGES[sale.status] || { color: 'bg-gray-100 text-gray-800', label: sale.status };
            return (
              <Card key={sale.sale_id} className="border border-[#E2E8F0]" data-testid={`dispatch-row-${sale.sale_id}`}>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                    {/* Orden */}
                    <div className="lg:col-span-1 flex flex-col items-center">
                      <span className="text-xs text-[#94A3B8] uppercase">Orden</span>
                      <div className="flex items-center gap-1 mt-1">
                        <Hash size={12} className="text-primary" />
                        <Input
                          type="number"
                          defaultValue={sale.dispatch_order || idx + 1}
                          onBlur={(e) => updateOrder(sale.sale_id, e.target.value)}
                          className="w-14 h-8 text-center font-bold text-lg p-0"
                          data-testid={`order-input-${sale.sale_id}`}
                        />
                      </div>
                    </div>

                    {/* Cliente + dirección */}
                    <div className="lg:col-span-5">
                      <p className="font-mono text-xs text-[#94A3B8]">{sale.invoice_number || sale.sale_id.slice(0, 8)}</p>
                      <p className="font-bold text-lg text-[#022C22]">{client?.name || 'Cliente desconocido'}</p>
                      <p className="text-xs text-[#64748B]">📞 {client?.phone || '-'}</p>
                      <div className="flex items-start gap-2 mt-2 text-sm">
                        <MapPin size={14} className="mt-0.5 text-primary flex-shrink-0" />
                        <span className="text-[#022C22]">{client?.address || 'Sin dirección'}</span>
                      </div>
                    </div>

                    {/* Resumen */}
                    <div className="lg:col-span-2">
                      <p className="text-xs text-[#94A3B8] uppercase">Ítems</p>
                      <p className="font-semibold">{sale.items?.length || 0} producto(s)</p>
                      <p className="text-xs text-[#64748B] mt-1">
                        {sale.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0} unidades
                      </p>
                    </div>

                    {/* Estado */}
                    <div className="lg:col-span-2">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                        {badge.label}
                      </span>
                      <p className="text-xs text-[#94A3B8] mt-2">
                        {new Date(sale.created_at).toLocaleString()}
                      </p>
                    </div>

                    {/* Acciones */}
                    <div className="lg:col-span-2 flex flex-col gap-2">
                      <Button
                        size="sm"
                        onClick={() => openMaps(client)}
                        disabled={!client}
                        data-testid={`maps-${sale.sale_id}`}
                      >
                        <ExternalLink size={14} className="mr-1" /> Ruta
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadDispatchTicket(sale.sale_id, sale.invoice_number)}
                        data-testid={`dispatch-ticket-${sale.sale_id}`}
                      >
                        <FileText size={14} className="mr-1" /> Guía
                      </Button>
                      {sale.status === 'procesada' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(sale.sale_id, 'en_transito')} data-testid={`start-${sale.sale_id}`}>
                          Iniciar
                        </Button>
                      )}
                      {sale.status === 'en_transito' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(sale.sale_id, 'despachado')} data-testid={`done-${sale.sale_id}`}>
                          Entregado
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
